import { OutlinedButton } from '@/components/layouts/Buttons/OutlinedButton/OutlinedButton';
import { useFetcherWithUtils } from '@/hooks/useFetcherWithUtils';
import { PermissionType } from '@/types/auth';
import s from './OrganizationSettings.module.scss';
import { FetcherMultiErrorResponse, WorkspaceSettingsLoaderData } from '@/types/types';
import { useGetPermissionsLevels } from '@/utils/helpers';
import { useLoaderData } from 'react-router-dom';
import { useUserPermissionsStore } from '@/stores/userPermissionsStore';
import { useOrganizationStore } from '@/stores/organizationStore';
import { OrganizationsService } from '@/services/organizationsService';
import { EntitySettingsInfo } from '@/components/Settings/EntitySettingsInfo/EntitySettingsInfo';
import { EntityUserManagement } from '@/components/Views/EntityUserManagement';
import { DeleteEntity } from '@/components/Settings/DeleteEntity/DeleteEntity';
import { OrganizationWorkspacesManagement } from '@/components/Views/OrganizationWorkspacesManagement';
import { WorkspaceFromStore } from '@/types/workspaces';
import { Timestamp } from 'firebase/firestore';
import { assert } from '@/utils/asserts';
import { useOrganizationInvitations } from '@/services/queries/useOrganizationInvitations';
import { useOrganizationPermissions } from '@/services/queries/useOrganizationPermissions';

export function OrganizationSettingsView() {
  const [, , isAdmin] = useGetPermissionsLevels(PermissionType.Organization);
  const { subscriptionDetails } = useLoaderData() as WorkspaceSettingsLoaderData;
  const [fetcher, isLoading] = useFetcherWithUtils<FetcherMultiErrorResponse>();
  const [deleteOrganizationFetcher, deleteOrganizationIsLoading] =
    useFetcherWithUtils<FetcherMultiErrorResponse>();
  const userId = useUserPermissionsStore((state) => state.userId) as NonNullable<string>;
  const selectedOrganization = useOrganizationStore(
    (state) => state.selectedOrganization,
  );
  assert(!!selectedOrganization, 'No organization selected');

  const permissions = useOrganizationPermissions(selectedOrganization.id);
  const invitations = useOrganizationInvitations(selectedOrganization.id);

  const handleLeaveOrganization = () => {
    const formData = new FormData();
    formData.append('type', 'permission');
    formData.append('permissionId', userId);
    fetcher.submit(formData, { method: 'delete' });
  };

  const handleDeleteOrganization = () => {
    const formData = new FormData();
    formData.append('type', 'organization');
    formData.append('organizationId', selectedOrganization?.id as string);
    deleteOrganizationFetcher.submit(formData, { method: 'delete' });
  };

  return (
    <>
      <EntitySettingsInfo
        isAdmin={isAdmin}
        createdAt={subscriptionDetails?.createdAt as Timestamp}
        subscriptionTier={subscriptionDetails?.subscriptionTier as string}
        userEmail={subscriptionDetails?.userEmail as string}
        userName={subscriptionDetails?.userName as string}
        isSubscription={!!subscriptionDetails}
        entityId={selectedOrganization?.id as string}
        entityType="organizations"
        entityName={selectedOrganization?.name as string}
        showUserOptions={isAdmin}
        isOrganization
        subscriptionUserId={subscriptionDetails?.userId as string}
      >
        <>
          <EntityUserManagement
            entityId={selectedOrganization?.id as string}
            changeRole={OrganizationsService.changeRole}
            changeInvitationRole={OrganizationsService.changeInvitationRole}
            deleteInvitation={OrganizationsService.deleteInvitation}
            deletePermission={OrganizationsService.deletePermission}
            isAdmin={isAdmin}
            invitations={invitations}
            permissions={permissions}
          />
        </>
      </EntitySettingsInfo>

      <hr style={{ width: '100%', marginTop: '1rem' }} />

      <OrganizationWorkspacesManagement
        organizationWorkspaces={selectedOrganization?.workspaces as WorkspaceFromStore[]}
      />

      <hr style={{ width: '100%', marginTop: '1rem' }} />

      <OutlinedButton
        isLoading={isLoading}
        onClick={handleLeaveOrganization}
        className={s.leaveButton}
        spinnerSize="20"
        spinnerColor="red"
      >
        Leave Organization
      </OutlinedButton>

      <DeleteEntity
        deleteEntity={handleDeleteOrganization}
        entityType="organizations"
        isLoading={deleteOrganizationIsLoading}
      />
    </>
  );
}
